// API 客户端：对 A 阶段 Express 后端做链式包装
// - 所有请求自动带 credentials: "include"，HttpOnly cookie 由浏览器自动带
// - R26 起不再从 JS 里读 cookie 塞 x-xyt-session-token header —— cookie 已 HttpOnly
//   读不到；后端 auth.js extractToken 会从 cookie 兜底解析，链路等价
// - 错误统一抛 ApiError
//
// 用法示例：
//   const rows = await api.from("projects").select("*").order("created_at", { ascending: false }).list();
//   const row = await api.from("projects").insert({ product_project: "...", ... }).single();
//   await api.from("projects").update({ today_status: "完成" }).eq("id", id).run();
//   await api.from("projects").delete().eq("id", id).run();
//   // R67：乐观并发 —— 长编辑表单 submit 时带上加载时的 version，避免"两人并发覆盖"
//   await api.from("iterations").update(patch).eq("id", id).ifMatch(row.version).run();
//   const data = await api.rpc("xyt_login", { p_username, p_password, p_session_hours: 12 });

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(msg: string, code: string, status: number) {
    super(msg);
    this.code = code;
    this.status = status;
  }
}

/**
 * R67：判断是否为乐观并发版本冲突（对应 If-Match / p_expected_version 不匹配）。
 * 服务端返回 409 + code=VERSION_CONFLICT。mutation 的 onError 可用它决定是否
 * 需要 invalidate 缓存，让 UI 强制刷新到最新服务端状态。
 */
export function isVersionConflict(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409 && err.code === "VERSION_CONFLICT";
}

// -------- 401 全局钩子 --------
// Providers 在挂载时注册一个处理器，用于：
//   1) 清理 session cookie
//   2) toast 提示
//   3) router.push("/login?next=<当前路径>")
// 避免 api-client 直接耦合 next/router 和 sonner
type UnauthorizedHandler = (err: ApiError) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
// 同一次会话过期只触发一次（避免每次请求都 toast 轰炸）
let unauthorizedFired = false;

export function setUnauthorizedHandler(h: UnauthorizedHandler | null) {
  unauthorizedHandler = h;
  unauthorizedFired = false;
}

async function httpRequest<T>(method: string, url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // R26：token 已是 HttpOnly cookie，浏览器通过 credentials:"include" 自动带；
  // 不再从 JS 读 cookie 塞 header（cookie 已 HttpOnly，想读也读不到）。

  const resp = await fetch(url, {
    ...init,
    method,
    headers,
    credentials: "include",
  });

  // 304：未变，调用方自己处理
  if (resp.status === 304) {
    return undefined as unknown as T;
  }

  let json: any = null;
  const text = await resp.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // 非 JSON 响应
    }
  }

  if (!resp.ok) {
    const err = json?.error || {};
    const apiErr = new ApiError(
      err.message || resp.statusText || "请求失败",
      err.code || `HTTP_${resp.status}`,
      resp.status
    );
    // 401 全局钩子：session 过期 / 未登录，通知上层清理并跳登录
    if (resp.status === 401 && unauthorizedHandler && !unauthorizedFired) {
      unauthorizedFired = true;
      try { unauthorizedHandler(apiErr); } catch { /* 静默 */ }
    }
    throw apiErr;
  }
  return (json?.data ?? json) as T;
}

// ============================================================
// Query Builder（PostgREST 风格）
// ============================================================
type Filter = { col: string; op: string; value: unknown };
type Order = { col: string; ascending: boolean };

class TableQuery<Row = Record<string, unknown>> {
  private _filters: Filter[] = [];
  private _orders: Order[] = [];
  private _limit?: number;
  private _offset?: number;
  private _select = "*";
  private _mode: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  private _payload: Row | Row[] | null = null;
  private _upsertOnConflict?: string;
  // R67：乐观并发 —— update/delete 携带 If-Match: "<version>" 做版本校验
  private _ifMatch?: number;

  constructor(private table: string) {}

  // --- 过滤 ---
  eq(col: string, value: unknown) { this._filters.push({ col, op: "eq", value }); return this; }
  neq(col: string, value: unknown) { this._filters.push({ col, op: "neq", value }); return this; }
  gt(col: string, value: unknown) { this._filters.push({ col, op: "gt", value }); return this; }
  gte(col: string, value: unknown) { this._filters.push({ col, op: "gte", value }); return this; }
  lt(col: string, value: unknown) { this._filters.push({ col, op: "lt", value }); return this; }
  lte(col: string, value: unknown) { this._filters.push({ col, op: "lte", value }); return this; }
  ilike(col: string, pattern: string) { this._filters.push({ col, op: "ilike", value: pattern }); return this; }
  in(col: string, arr: unknown[]) { this._filters.push({ col, op: "in", value: arr }); return this; }
  is(col: string, value: null | true | false) { this._filters.push({ col, op: "is", value }); return this; }

  // --- 排序 + 分页 ---
  order(col: string, opts: { ascending?: boolean } = {}) {
    this._orders.push({ col, ascending: opts.ascending !== false });
    return this;
  }
  limit(n: number) { this._limit = n; return this; }
  range(from: number, to: number) { this._offset = from; this._limit = to - from + 1; return this; }

  // --- 投影 ---
  select(cols: string = "*") { this._select = cols; return this; }

  // --- 写入动作 ---
  insert(data: Row | Row[]) { this._mode = "insert"; this._payload = data; return this; }
  update(data: Partial<Row>) { this._mode = "update"; this._payload = data as Row; return this; }
  delete() { this._mode = "delete"; return this; }
  upsert(data: Row | Row[], opts: { onConflict?: string } = {}) {
    this._mode = "upsert";
    this._payload = data;
    this._upsertOnConflict = opts.onConflict;
    return this;
  }
  /**
   * R67：乐观并发 —— 在 update() / delete() 链式调用末尾 .ifMatch(version)
   * 会把 `If-Match: "<version>"` 放进请求头。服务端若发现当前行 version
   * 不等于该值，返回 409 VERSION_CONFLICT，前端可提示"数据已被他人修改"。
   * 传 0 / NaN 不生效，避免误发。
   */
  ifMatch(version: number | null | undefined) {
    if (version != null && Number.isFinite(Number(version))) {
      this._ifMatch = Number(version);
    }
    return this;
  }

  // --- 终止动作：把自己变成 fetch ---
  private buildQueryString(): string {
    const qp = new URLSearchParams();
    if (this._select && this._select !== "*") qp.set("select", this._select);
    for (const f of this._filters) {
      if (f.op === "in" && Array.isArray(f.value)) {
        qp.append(f.col, `in.(${f.value.map((v) => String(v)).join(",")})`);
      } else if (f.op === "is") {
        qp.append(f.col, `is.${f.value === null ? "null" : String(f.value)}`);
      } else {
        qp.append(f.col, `${f.op}.${String(f.value)}`);
      }
    }
    for (const o of this._orders) qp.append("order", `${o.col}.${o.ascending ? "asc" : "desc"}`);
    if (this._limit != null) qp.set("limit", String(this._limit));
    if (this._offset != null) qp.set("offset", String(this._offset));
    const s = qp.toString();
    return s ? `?${s}` : "";
  }

  // 终止：列表
  async list(): Promise<Row[]> {
    if (this._mode !== "select") throw new Error("list() 只能用于 select 模式");
    return await httpRequest<Row[]>("GET", `/api/tables/${this.table}${this.buildQueryString()}`);
  }

  // 终止：计数（走 /api/tables/:table/count）
  async count(): Promise<number> {
    if (this._mode !== "select") throw new Error("count() 只能用于 select 模式");
    const resp = await httpRequest<{ count: number }>(
      "GET",
      `/api/tables/${this.table}/count${this.buildQueryString()}`
    );
    return resp?.count ?? 0;
  }

  // 终止：单行（没找到抛错）
  async single(): Promise<Row> {
    if (this._mode === "select") {
      const rows = await this.list();
      if (!rows || rows.length === 0) throw new ApiError("没有匹配的记录", "NOT_FOUND", 404);
      return rows[0];
    }
    if (this._mode === "insert") return this.insertRun<Row>({ single: true });
    if (this._mode === "upsert") return this.upsertRun<Row>({ single: true });
    if (this._mode === "update") return this.updateRun<Row>({ single: true });
    throw new Error("single() 无法应用于当前模式");
  }

  // 终止：可选单行（没找到返回 null）
  async maybeSingle(): Promise<Row | null> {
    try {
      return await this.single();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  }

  // 终止：写入（统一名称 run，返回受影响行）
  async run(): Promise<Row[]> {
    if (this._mode === "insert") return this.insertRun<Row[]>();
    if (this._mode === "upsert") return this.upsertRun<Row[]>();
    if (this._mode === "update") return this.updateRun<Row[]>();
    if (this._mode === "delete") return this.deleteRun();
    throw new Error("run() 仅用于写入模式");
  }

  private async insertRun<T>(opts: { single?: boolean } = {}): Promise<T> {
    const rows = await httpRequest<any[]>("POST", `/api/tables/${this.table}`, {
      body: JSON.stringify(this._payload),
    });
    return (opts.single ? rows?.[0] : rows) as T;
  }

  private async upsertRun<T>(opts: { single?: boolean } = {}): Promise<T> {
    const qp = new URLSearchParams();
    if (this._upsertOnConflict) qp.set("on_conflict", this._upsertOnConflict);
    const s = qp.toString();
    const rows = await httpRequest<any[]>(
      "POST",
      `/api/tables/${this.table}/upsert${s ? "?" + s : ""}`,
      { body: JSON.stringify(this._payload) }
    );
    return (opts.single ? rows?.[0] : rows) as T;
  }

  private async updateRun<T>(opts: { single?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = {};
    if (this._ifMatch != null) headers["If-Match"] = `"${this._ifMatch}"`;
    const rows = await httpRequest<any[]>("PATCH", `/api/tables/${this.table}${this.buildQueryString()}`, {
      body: JSON.stringify(this._payload),
      headers,
    });
    return (opts.single ? rows?.[0] : rows) as T;
  }

  private async deleteRun(): Promise<any> {
    const headers: Record<string, string> = {};
    if (this._ifMatch != null) headers["If-Match"] = `"${this._ifMatch}"`;
    return await httpRequest("DELETE", `/api/tables/${this.table}${this.buildQueryString()}`, { headers });
  }
}

// ============================================================
// 顶层客户端
// ============================================================
export const api = {
  from<Row = Record<string, unknown>>(table: string) {
    return new TableQuery<Row>(table);
  },
  async rpc<T = unknown>(fnName: string, args: Record<string, unknown> = {}): Promise<T> {
    return await httpRequest<T>("POST", `/api/rpc/${fnName}`, { body: JSON.stringify(args) });
  },
  // 直接 fetch，给特殊场景用
  http: httpRequest,
};

export type Api = typeof api;
